import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Loader2,
  Rocket,
  Check,
  Sparkles,
  Zap,
  X,
  ImageIcon,
  Camera,
} from "lucide-react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { uploadFileToS3, validateFile } from "@/utils/aws";
import api from "@/redux/axios";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  uploadEvaluation,
  setUploadProgress,
  setUploading,
  setFileUrl,
  resetEvaluateState,
  evaluateSelector,
  clearError,
  clearSuccess,
} from "./evaluateSlice";
import { websocketManager } from "@/utils/websocket";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import ImageReviewModal from "./ImageReviewModal";
import EvaluateTutorial from "./EvaluateTutorial";
import { HelpCircle } from "lucide-react";

const Evaluate = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Tutorial state
  const [runTutorial, setRunTutorial] = useState(false);

  // Check if it's the first time visiting the page to auto-run tutorial
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem("hasSeenEvaluateTutorial");
    if (!hasSeenTutorial) {
      // Small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        setRunTutorial(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTutorialComplete = () => {
    setRunTutorial(false);
    localStorage.setItem("hasSeenEvaluateTutorial", "true");
  };
  const {
    isUploading,
    isSubmitting,
    uploadProgress,
    isError,
    errorMessage,
    isSuccess,
    evaluationId,
    websocket,
  } = useAppSelector(evaluateSelector);

  const [analyzing, setAnalyzing] = useState(false);
  const [fileName, setFileName] = useState("");
  const [examType, setExamType] = useState<string>("UPSC");
  const [subject, setSubject] = useState<string>("GS1");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [facts, setFacts] = useState<any[]>([]);
  const [statusIndex, setStatusIndex] = useState(0);
  const statusMessages = [
    "Evaluating",
    "Analyzing",
    "Processing",
    "Reviewing",
    "Checking"
  ];
  const [credits, setCredits] = useState<number | null>(null);

  // const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  // Image Upload State
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [showImageReviewModal, setShowImageReviewModal] = useState(false);
  // Array to track selection ORDER (first clicked = index 0, etc.) - Instagram style
  const [selectedImageOrder, setSelectedImageOrder] = useState<number[]>([]);
  // isConvertingToPdf state is managed inside ImageReviewModal

  // New state for carousel
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Drag and drop state for image reordering (passed to ImageReviewModal)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Auto-submit state for camera capture
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);

  const scaleIn = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.4 } }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use ref to track latest WebSocket state for cleanup function
  const websocketStateRef = useRef(websocket);
  useEffect(() => {
    websocketStateRef.current = websocket;
  }, [websocket]);

  // Sync local analyzing state
  useEffect(() => {
    if (websocket.isProcessing && websocket.taskId && !analyzing) {
      setAnalyzing(true);
    }
  }, [websocket.isProcessing, websocket.taskId, analyzing]);

  const isWebSocketProcessing = websocket.isProcessing && websocket.taskId;
  const isEvaluating = analyzing || isWebSocketProcessing;

  // Prevent refresh/navigation
  useEffect(() => {
    if (!isEvaluating) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = "";
      return "Evaluation in progress. Changes may not be saved.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEvaluating]);


  // Carousel Logic
  useEffect(() => {
    if (!isEvaluating || facts.length === 0) return;

    const duration = 5000; // 8 seconds per slide
    const intervalTime = 100;
    const increment = (intervalTime / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentFactIndex((prevIndex) => (prevIndex + 1) % facts.length);
          return 0;
        }
        return prev + increment;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [isEvaluating, facts.length]);

  // Reset progress when index changes manually or automatically
  useEffect(() => {
    setProgress(0);
  }, [currentFactIndex]);


  // Reset state on unmount logic
  useEffect(() => {
    return () => {
      const currentWebsocket = websocketStateRef.current;
      if (!currentWebsocket.isProcessing && !currentWebsocket.taskId) {
        dispatch(resetEvaluateState());
      }
    };
  }, [dispatch]);

  // Handle WebSocket completion
  useEffect(() => {
    if (websocket.isComplete && websocket.evaluationId) {
      const evalId = websocket.evaluationId;

      // Disconnect WebSocket first
      websocketManager.disconnect();

      // Only navigate on successful WebSocket completion with ID
      navigate(`/evaluation/${evalId}`, { replace: true });

      setTimeout(() => {
        setAnalyzing(false);
        setUploadedFile(null);
        setUploadedImages([]);
        setFileName("");
        setSubject("");
        setExamType("UPSC");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        dispatch(resetEvaluateState());
      }, 1000);
    } else if (
      websocket.isComplete &&
      websocket.taskId &&
      !websocket.evaluationId
    ) {
      const hasError = websocket.events.some((event: any) => {
        const message = event.data?.message || "";
        return (
          message &&
          (message.toLowerCase().includes("error") ||
            message.toLowerCase().includes("failed") ||
            message.toLowerCase().includes("occurred"))
        );
      });

      if (hasError) {
        const errorEvent = websocket.events.find((event: any) => {
          const message = event.data?.message || "";
          return (
            message &&
            (message.toLowerCase().includes("error") ||
              message.toLowerCase().includes("failed") ||
              message.toLowerCase().includes("occurred"))
          );
        });
        const errorMessage =
          errorEvent?.data?.message || "An error occurred during evaluation. Please try a different file.";

        // Show local dialog instead of toast and navigation
        setErrorDialogMessage(errorMessage);
        setShowErrorDialog(true);

        dispatch(resetEvaluateState());
        websocketManager.disconnect();
        setAnalyzing(false);
        setUploadedFile(null);
        setFileName("");
        // Keep subject and exam type selected for better UX
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setTimeout(() => {
          dispatch(resetEvaluateState());
          websocketManager.disconnect();
          setAnalyzing(false);
          setUploadedFile(null);
          setUploadedImages([]);
          setFileName("");
          setSubject("");
          setExamType("UPSC");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          toast.success("Processing completed!");
        }, 2000);
      }
    }
  }, [
    websocket.isComplete,
    websocket.taskId,
    websocket.evaluationId,
    websocket.events,
    dispatch,
    navigate,
  ]);

  // Fetch user credits
  const fetchCredits = async () => {
    try {
      const res = await api.get("/credits");
      if (res?.data) {
        setCredits(res.data.credits_remaining ?? 0);
      }
    } catch (err: any) {
      console.warn("Could not fetch credits:", err?.message || err);
      setCredits(0);
    }
  };

  // Handle success/error messages
  useEffect(() => {
    if (isSuccess && evaluationId && !websocket.isProcessing) {
      // Do not navigate here. Wait for WebSocket completion.
      // toast.success("Evaluation uploaded successfully!");
      dispatch(clearSuccess());
      fetchCredits();
      // navigate(`/evaluation/${evaluationId}`); 
    } else if (isSuccess && !evaluationId && !websocket.isProcessing) {
      toast.success("Evaluation submitted successfully!");
      dispatch(clearSuccess());
      setAnalyzing(false);
      fetchCredits();
    }
    if (isError && errorMessage) {
      toast.error(errorMessage, { id: "evaluation-error" });
      dispatch(clearError());
      dispatch(setUploading(false));
      setAnalyzing(false);
      websocketManager.disconnect();
      fetchCredits();
    }
  }, [
    isSuccess,
    isError,
    errorMessage,
    evaluationId,
    dispatch,
    navigate,
    websocket.isProcessing,
  ]);

  // Fetch facts
  const fetchFacts = async () => {
    try {
      const res = await api.get(`/api/current-affair-facts`, {
        params: { limit: 20 },
      });
      if (res?.data?.facts) {
        setFacts(res.data.facts.slice(0, 20));
      }
    } catch (err: any) {
      console.warn("Could not fetch facts:", err?.message || err);
    }
  };

  useEffect(() => {
    if (analyzing) {
      fetchFacts();
    }
  }, [analyzing]);

  useEffect(() => {
    let statusInterval: ReturnType<typeof setInterval> | undefined;
    if (analyzing) {
      statusInterval = setInterval(() => {
        setStatusIndex((prev) => (prev + 1) % statusMessages.length);
      }, 3000);
    }
    return () => {
      if (statusInterval) clearInterval(statusInterval);
    };
  }, [analyzing, statusMessages.length]);

  useEffect(() => {
    fetchCredits();
  }, []);

  // Handle file/images passed from Camera Page
  useEffect(() => {
    if (location.state?.capturedImages) {
      // Handle images from camera - open review modal
      const images = location.state.capturedImages as File[];
      setUploadedImages(images);
      setUploadedFile(null);
      setFileName("");
      // Select all images by default in order
      setSelectedImageOrder(images.map((_, idx) => idx));

      // Restore exam type and subject if passed
      if (location.state.examType) setExamType(location.state.examType);
      if (location.state.subject) setSubject(location.state.subject);

      // Open review modal if flag is set
      if (location.state.openReviewModal) {
        setTimeout(() => {
          setShowImageReviewModal(true);
        }, 100);
      }

      // Clear history state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
      toast.success("Images loaded! Review and create PDF.");
    } else if (location.state?.capturedFile) {
      const file = location.state.capturedFile as File;
      setFileName(file.name);
      setUploadedFile(file);
      setUploadedImages([]); // Clear any existing images

      // Restore exam type and subject if passed
      if (location.state.examType) setExamType(location.state.examType);
      if (location.state.subject) setSubject(location.state.subject);

      // Auto-evaluate if flag is set
      if (location.state.autoEvaluate) {
        setShouldAutoSubmit(true);
      }

      // Clear history state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
      toast.success("Scanned document loaded!");
    } else if (location.state?.examType || location.state?.subject) {
      // Restore selections when returning without a file
      if (location.state.examType) setExamType(location.state.examType);
      if (location.state.subject) setSubject(location.state.subject);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-submit effect for camera capture
  useEffect(() => {
    if (shouldAutoSubmit && uploadedFile) {
      setShouldAutoSubmit(false);
      setTimeout(() => {
        handleEvaluate();
      }, 100);
    }
  }, [shouldAutoSubmit, uploadedFile]);

  const examTypes = ["UPSC", "MPPSC", "UPPCS", "BPSC"];
  const subjects = ["GS1", "GS2", "GS3", "GS4", "Essay"];



  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const MAX_SIZE_MB = 25;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    // Handle multiple files (assuming images) or single PDF
    const newImages: File[] = [];
    let pdfFile: File | null = null;

    Array.from(files).forEach((file: File) => {
      if (file.type === "application/pdf") {
        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`File ${file.name} exceeds the ${MAX_SIZE_MB}MB limit`);
          return;
        }
        pdfFile = file;
      } else if (file.type.startsWith("image/")) {
        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`File ${file.name} exceeds the ${MAX_SIZE_MB}MB limit`);
          return;
        }
        const validation = validateFile(file, 22);
        if (!validation.valid) {
          toast.error(`File ${file.name}: ${validation.error || "Invalid file"}`);
          return;
        }
        newImages.push(file);
      } else {
        toast.error(`File ${file.name} is not supported. Only PDF and Images are allowed.`);
      }
    });

    if (pdfFile) {
      // Prioritize PDF if present, or handle mixed? Assuming single PDF replaces everything.
      const finalPdf: File = pdfFile;
      setFileName(finalPdf.name);
      setUploadedFile(finalPdf);
      setUploadedImages([]); // Clear images if PDF selected
    } else if (newImages.length > 0) {
      setUploadedImages(prev => [...prev, ...newImages]);
      setUploadedFile(null); // Clear PDF if images selected
      // Pre-select all new images
      // Auto-select new images in order
      setSelectedImageOrder(prev => {
        const currentLength = uploadedImages.length;
        const newIndices = newImages.map((_, i) => currentLength + i);
        return [...prev, ...newIndices];
      });
      setFileName(`${uploadedImages.length + newImages.length} images selected`);
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const convertImagesToPdf = async (images: File[]): Promise<File> => {
    return new Promise((resolve, reject) => {
      try {
        const doc = new jsPDF();


        // Process sequentially to maintain order
        const processAll = async () => {
          for (let i = 0; i < images.length; i++) {
            // We need to read the file to data URL
            await new Promise<void>((resolveImg, rejectImg) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                const imgData = e.target?.result as string;

                const img = new Image();
                img.src = imgData;
                img.onload = () => {
                  const pdfWidth = doc.internal.pageSize.getWidth();
                  const pdfHeight = (img.height * pdfWidth) / img.width;

                  // If height is larger than page, scale it down or split? 
                  // For now, simple scaling to width, let height expand (but JS PDF page is fixed A4 usually).
                  // Better: Scale to fit A4 page.
                  const pageHeight = doc.internal.pageSize.getHeight();
                  let finalWidth = pdfWidth;
                  let finalHeight = pdfHeight; // default

                  // If purely scaling by width gives a height > page, we might need to shrink or just accept it (multipage logic is complex).
                  // But usually we want to fit specific content.
                  // Simplest robust logic: fit within page dimensions maintaining aspect ratio.
                  const pageRatio = pageHeight / pdfWidth;
                  const imgRatio = img.height / img.width;

                  if (imgRatio > pageRatio) {
                    // Image is taller than page relative to width -> constrain by height
                    finalHeight = pageHeight;
                    finalWidth = finalHeight / imgRatio;
                  } else {
                    // Image is wider -> constrain by width
                    finalWidth = pdfWidth;
                    finalHeight = finalWidth * imgRatio;
                  }

                  // Centering
                  const x = (pdfWidth - finalWidth) / 2;
                  const y = 0; // Top aligned

                  if (i > 0) doc.addPage();
                  doc.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
                  resolveImg();
                };
                img.onerror = rejectImg;
              };
              reader.onerror = rejectImg;
              reader.readAsDataURL(images[i]);
            });
          }
          const pdfBlob = doc.output('blob');
          const pdfFile = new File([pdfBlob], "submission.pdf", { type: "application/pdf" });
          resolve(pdfFile);
        };

        processAll().catch(reject);

      } catch (err) {
        reject(err);
      }
    });
  };

  const handleQuickAnalysis = async () => {
    if (!examType || !subject) {
      toast.error("Please select exam type and subject");
      return;
    }
    if (uploadedImages.length === 0) {
      toast.error("Please upload images");
      return;
    }

    try {
      const imagesToProcess =
        selectedImageOrder.length > 0
          ? (selectedImageOrder.map(i => uploadedImages[i]).filter(Boolean) as File[])
          : uploadedImages;

      const pdf = await convertImagesToPdf(imagesToProcess);
      setUploadedFile(pdf);
      setFileName(pdf.name);
      setUploadedImages([]);
      setSelectedImageOrder([]);
      await handleEvaluate(pdf);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create PDF");
    }
  };

  const handleEvaluate = async (fileOverride?: File) => {
    const fileToProcess = fileOverride || uploadedFile;

    if (!examType || !subject) {
      toast.error("Please select exam type and subject");
      return;
    }
    if (!fileToProcess) {
      toast.error("Please upload a file");
      return;
    }

    const validation = validateFile(fileToProcess, 22);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid file");
      return;
    }

    try {
      dispatch(setUploading(true));
      dispatch(setUploadProgress(0));

      const { fileUrl: s3FileUrl } = await uploadFileToS3(
        fileToProcess,
        (progress) => {
          dispatch(setUploadProgress(progress));
        }
      );

      dispatch(setFileUrl(s3FileUrl));
      toast.success("File uploaded successfully!");

      dispatch(setUploading(false));
      setAnalyzing(true);

      const apiPayload = {
        file_url: s3FileUrl,
        paper: subject,
        exam_type: examType,
      };

      await dispatch(uploadEvaluation(apiPayload)).unwrap();
      fetchCredits();

    } catch (error: any) {
      console.error("❌ Evaluation failed in handleEvaluate:", error);
      const errorMsg =
        error?.message ||
        error?.errorMessage ||
        error?.data?.message ||
        "Failed to process evaluation. Please try again.";

      toast.error(errorMsg, { id: "evaluation-error" });
      dispatch(setUploading(false));
      setAnalyzing(false);
      dispatch(resetEvaluateState());
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const MAX_SIZE_MB = 25;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const newImages: File[] = [];
    let pdfFile: File | null = null;

    Array.from(files).forEach((file: File) => {
      if (file.type === "application/pdf") {
        if (file.size <= MAX_SIZE_BYTES) pdfFile = file;
      } else if (file.type.startsWith("image/")) {
        if (file.size <= MAX_SIZE_BYTES) newImages.push(file);
      }
    });

    if (pdfFile) {
      // Explicit check to satisfy TS if needed
      const finalPdf: File = pdfFile;
      setFileName(finalPdf.name);
      setUploadedFile(finalPdf);
      setUploadedImages([]);
    } else if (newImages.length > 0) {
      setUploadedImages(prev => [...prev, ...newImages]);
      setUploadedFile(null);
      // Auto-select new images in order
      setSelectedImageOrder(prev => {
        const currentLength = uploadedImages.length;
        const newIndices = newImages.map((_, i) => currentLength + i);
        return [...prev, ...newIndices];
      });
      setFileName(`${uploadedImages.length + newImages.length} images selected`);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  if (isUploading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={scaleIn}
          className="max-w-xl w-full"
        >
          <div className="bg-white rounded-[32px] shadow-xl p-8 md:p-12 text-center border border-gray-100">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                Uploading Your File...
              </h1>

              <div className="w-full max-w-sm mx-auto space-y-3">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-indigo-600">Uploading</span>
                  <span className="text-gray-900">{uploadProgress}%</span>
                </div>
                <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
                  <motion.div
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ type: "spring", stiffness: 50, damping: 20 }}
                  />
                </div>
              </div>

              <p className="text-gray-500 text-sm font-medium">
                Please wait while we secure your answer sheet...
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (isEvaluating) {
    return (
      <div className="min-h-screen bg-white pt-6 md:pt-16 flex flex-col items-center px-4 md:p-6 relative overflow-hidden">

        {/* Background Ambient Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-indigo-50/50 rounded-full blur-[120px]" />
          <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] bg-purple-50/50 rounded-full blur-[100px]" />
        </div>

        {/* Main Centered Content */}
        <div className="relative z-10 flex flex-col items-center max-w-5xl w-full text-center mt-4 md:mt-10 lg:mt-0">

          {/* Main Title */}
          <AnimatePresence mode="wait">
            <motion.h1
              key={statusIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-3xl md:text-6xl font-black tracking-tight text-indigo-600 mb-3 md:mb-6 leading-tight"
            >
              {statusMessages[statusIndex]}
              <span className="inline-flex ml-1">
                {[0, 1, 2].map((dot) => (
                  <motion.span
                    key={dot}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      times: [0, 0.5, 1],
                      delay: dot * 0.2,
                    }}
                  >
                    .
                  </motion.span>
                ))}
              </span>
            </motion.h1>
          </AnimatePresence>

          {/* Subtitle */}
          <p className="text-xs text-amber-800 font-medium">Evaluation in progress. Do not refresh.</p>
        </div>

        {/* Engagement Section: Carousel Cards */}
        <div className="w-full relative z-10 mt-6 md:mt-12 pb-8 md:pb-12 max-w-4xl mx-auto">
          <div className="mb-4 md:mb-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
              <div className="h-px bg-gray-200 w-8 md:w-12" />
              <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest">While you wait</span>
              <div className="h-px bg-gray-200 w-8 md:w-12" />
            </div>
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 tracking-tight">
              Explore Curated Insights
            </h2>
          </div>

          {facts && facts.length > 0 ? (
            <div className="relative">
              {/* Progress Bar */}
              <div className="w-full h-1 bg-gray-100 rounded-full mb-3 md:mb-6 overflow-hidden">
                <motion.div
                  className="h-full bg-indigo-500"
                  style={{ width: `${progress}%` }}
                  transition={{ ease: "linear", duration: 0.3 }}
                />
              </div>

              {/* Active Card */}
              <div className="relative min-h-[220px] md:h-52 overflow-hidden perspective-distant">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentFactIndex}
                    initial={{ opacity: 0, x: 50, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -50, scale: 0.95 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute inset-0 bg-white rounded-2xl md:rounded-3xl p-4 md:p-5 border border-gray-100 shadow-sm flex flex-col justify-between"
                  >
                    <div className="text-left">
                      <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">Did You Know?</h3>
                      <p className="text-sm md:text-lg text-gray-600 leading-relaxed font-medium line-clamp-6 md:line-clamp-none">
                        {facts[currentFactIndex].fact_text}
                      </p>
                    </div>
                    <div className="flex justify-between items-center text-xs md:text-sm text-gray-400 font-medium pt-3 md:pt-4 border-t border-gray-50 mt-3 md:mt-4">
                      <span>Fact {currentFactIndex + 1} of {facts.length}</span>
                      <div className="flex gap-3 md:gap-2">
                        <button
                          onClick={() => setCurrentFactIndex(prev => (prev - 1 + facts.length) % facts.length)}
                          className="hover:text-indigo-600 transition-colors px-2 py-1"
                        >Prev</button>
                        <button
                          onClick={() => setCurrentFactIndex(prev => (prev + 1) % facts.length)}
                          className="text-indigo-600 transition-colors px-2 py-1"
                        >Next</button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-32 text-gray-400 bg-gray-50 rounded-2xl">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <p>Loading curated facts...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const activeStep1 = !!examType;
  const activeStep2 = !!subject;
  const activeStep3 = !!uploadedFile || uploadedImages.length > 0;
  const isReadyToSubmit = activeStep1 && activeStep2 && activeStep3 && !isUploading && !isSubmitting && !analyzing && !isWebSocketProcessing;

  return (
    <>
      <div className="h-[95vh] bg-white flex">
        {/* Left Side - Hero/Image Section */}
        <div className="hidden lg:flex w-1/3 bg-gray-900 m-8 rounded-2xl relative flex-col justify-end p-8 overflow-hidden">
          {/* Background Image with Overlay */}
          <div
            className="absolute inset-0 z-0 opacity-60"
            style={{
              backgroundImage: "url('https://i.pinimg.com/736x/9d/99/63/9d9963ad8e1ec95fb1fd71a4267a7d16.jpg')",
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              // filter: 'grayscale(100%) contrast(120%)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent z-0" />

          {/* Content */}
          <div className="relative z-10 space-y-6 max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-xs font-bold text-white uppercase tracking-widest shadow-lg w-fit">
              <Sparkles className="w-3 h-3 text-amber-300" />
              AI Powered Analysis
            </div>
            <h1 className="text-5xl font-bold text-white tracking-tight leading-tight">
              Turn your answers into <span className="text-indigo-300">verified success.</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Get instant, detailed feedback on your mains answer sheets. Analyze structure, content, and relevance with our advanced AI engine.
            </p>

            <div className="pt-8 flex items-center gap-4">
              <div className="flex -space-x-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gray-800 border-2 border-black flex items-center justify-center text-xs font-medium text-gray-400">
                    U
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-400">
                <span className="text-white font-bold">1000+</span> evaluations today
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="w-full lg:w-1/2 flex flex-col bg-white h-full overflow-y-auto">
          <div className="max-w-xl mx-auto w-full p-8 lg:p-12 flex flex-col flex-1">

            {/* Header with Credits */}


            <div className="flex-1 flex flex-col justify-center space-y-3">
              <div className="flex justify-between items-center mb-4">

                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Start Evaluation</h2>
                  <p className="text-gray-500 mt-2">Upload your answer sheet to get started.</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => setRunTutorial(true)}
                    className="hidden lg:flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm transition-colors mb-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    How it works?
                  </button>
                  {/* Credits Badge */}
                  <div
                    id="tutorial-credits"
                    // onClick={() => setShowUpgradeModal(true)}
                    className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100 transition-colors"
                  >
                    <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="text-sm font-semibold text-gray-900">{credits ?? "..."} <span className="text-gray-400 font-normal">credits</span></span>
                  </div>
                </div>
              </div>
              {/* Mobile Tutorial Trigger */}
              <button
                onClick={() => setRunTutorial(true)}
                className="lg:hidden flex items-center gap-1.5 text-indigo-600 font-black text-[10px] uppercase tracking-[0.1em] mb-1"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Show Tutorial
              </button>

              {/* Step 1 & 2: Exam & Subject Selection */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-900">1. Select Exam</label>
                  <Select
                    value={examType}
                    onValueChange={setExamType}
                    disabled={isWebSocketProcessing}
                  >
                    <SelectTrigger id="tutorial-exam-select" className="w-full bg-indigo-500 border border-gray-200 text-white text-sm font-bold rounded-xl px-4 py-3 h-auto focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm">
                      <SelectValue placeholder="Select Exam" />
                    </SelectTrigger>
                    <SelectContent>
                      {examTypes.map((type) => (
                        <SelectItem key={type} value={type} disabled={type !== 'UPSC'}>
                          {type} {type !== 'UPSC' ? '(Coming Soon)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-900">2. Select Paper</label>
                  <Select
                    value={subject}
                    onValueChange={setSubject}
                    disabled={isWebSocketProcessing}
                  >
                    <SelectTrigger id="tutorial-paper-select" className="w-full bg-indigo-500 border border-gray-200 text-white text-sm font-bold rounded-xl px-4 py-3 h-auto focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm">
                      <SelectValue placeholder="Select Paper" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((subj) => (
                        <SelectItem key={subj} value={subj}>
                          {subj}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Step 3: Upload */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-gray-900">3. Upload Answer Copy</label>

                {/* Upload Options Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* File Upload Option */}
                  <div
                    id="tutorial-upload-zone"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                            border-2 border-dashed rounded-2xl transition-all duration-300 p-4 flex flex-col items-center justify-center text-center cursor-pointer group
                            ${uploadedFile || uploadedImages.length > 0
                        ? "border-emerald-500 bg-emerald-50/20"
                        : "border-gray-200 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300"
                      }
                         `}
                  >
                    <div className="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-2 shadow-sm group-hover:scale-105 transition-transform">
                      <Upload className="w-4 h-4 text-gray-600" />
                    </div>
                    <p className="text-sm font-bold text-gray-900">Upload File</p>
                    <p className="text-xs text-gray-500 mt-1">PDF or Images</p>
                  </div>

                  {/* Camera Scan Option */}
                  <div
                    id="tutorial-scan-pages"
                    onClick={() => navigate("/evaluate/camera", {
                      state: { examType, subject }
                    })}
                    className="border-2 border-dashed rounded-2xl transition-all duration-300 p-4 flex flex-col items-center justify-center text-center cursor-pointer group border-indigo-200 bg-indigo-50/30 hover:bg-indigo-50 hover:border-indigo-300"
                  >
                    <div className="w-10 h-10 bg-indigo-100 border border-indigo-200 rounded-full flex items-center justify-center mb-2 shadow-sm group-hover:scale-105 transition-transform">
                      <Camera className="w-4 h-4 text-indigo-600" />
                    </div>
                    <p className="text-sm font-bold text-gray-900">Scan Pages</p>
                    <p className="text-xs text-gray-500 mt-1">Use Camera</p>
                  </div>
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileSelect}
                  disabled={isWebSocketProcessing}
                />

                {/* File Status Display - Only show when file is uploaded */}
                {(uploadedFile || uploadedImages.length > 0) && (
                  <div className="border-2 border-emerald-500 bg-emerald-50/20 rounded-2xl p-4 flex flex-col items-center justify-center text-center">
                    <div className="w-full">
                      {uploadedFile ? (
                        <>
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Check className="w-5 h-5 text-emerald-600" />
                          </div>
                          <p className="text-sm font-bold text-gray-900 truncate">{fileName}</p>
                          <p className="text-xs text-gray-500 mb-2">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <ImageIcon className="w-5 h-5 text-indigo-600" />
                          </div>
                          <p className="text-sm font-bold text-gray-900">{uploadedImages.length} Images Selected</p>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                          setUploadedImages([]);
                          setSelectedImageOrder([]);
                          setFileName("");
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="mt-2 text-xs text-indigo-600 font-semibold hover:underline flex items-center justify-center gap-1 mx-auto transition-colors"
                      >
                        <X className="w-3 h-3" /> Clear Selection
                      </button>
                    </div>
                  </div>
                )}

                {/* Image Thumbnails Preview - Click to open review modal */}
                {uploadedImages.length > 0 && (
                  <div
                    className="grid grid-cols-4 gap-2 mt-4 animate-in fade-in slide-in-from-top-2 cursor-pointer"
                    onClick={() => setShowImageReviewModal(true)}
                  >
                    {uploadedImages.slice(0, 4).map((img, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-indigo-400 transition-colors">
                        <img src={URL.createObjectURL(img)} alt="preview" className="w-full h-full object-cover" />
                        {idx === 3 && uploadedImages.length > 4 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold">
                            +{uploadedImages.length - 4}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Evaluate Button */}
              <div className="pt-4">
                {uploadedImages.length > 0 ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-12 rounded-xl text-base font-bold border-2 border-indigo-500 text-indigo-600 hover:bg-indigo-50 transition-all"
                      onClick={() => setShowImageReviewModal(true)}
                      disabled={!isReadyToSubmit}
                    >
                      <span className="flex items-center gap-2">
                        Review Images <ImageIcon className="w-4 h-4" />
                      </span>
                    </Button>
                    <Button
                      id="tutorial-evaluate-button"
                      className={`
                               flex-1 h-12 rounded-xl text-base font-bold shadow-lg transition-all
                               ${!isReadyToSubmit
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                          : "bg-indigo-500 text-white hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5"
                        }
                            `}
                      onClick={handleQuickAnalysis}
                      disabled={!isReadyToSubmit}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" /> Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Start Analysis <Rocket className="w-4 h-4" />
                        </span>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    id="tutorial-evaluate-button"
                    className={`
                             w-full h-12 rounded-xl text-base font-bold shadow-lg transition-all
                             ${!isReadyToSubmit
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                        : "bg-indigo-500 text-white hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5"
                      }
                          `}
                    onClick={() => handleEvaluate()}
                    disabled={!isReadyToSubmit}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" /> Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Start Analysis <Rocket className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                )}
                <p className="text-center text-xs text-gray-400 mt-4">
                  By clicking Evaluate, you agree to our terms of service regarding AI analysis.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Credits Modal */}
        {/* <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-amber-500 fill-amber-500" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900 mb-2">
                Get More Credits
              </DialogTitle>
              <p className="text-gray-500 text-sm mb-6">
                Upgrade your plan to get unlimited evaluations and unlock premium features.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/pricing")}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700"
                >
                  View Plans
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full text-gray-500"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog> */}

      </div >

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900 mb-2">
              Evaluation Error
            </DialogTitle>
            <p className="text-gray-500 text-sm mb-6">
              {errorDialogMessage}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setShowErrorDialog(false);
                  dispatch(resetEvaluateState());
                  dispatch(setUploading(false));
                  setAnalyzing(false);
                  setUploadedFile(null);
                  setFileName("");
                  setUploadedImages([]);
                  setSelectedImageOrder([]);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowErrorDialog(false)}
                className="w-full text-gray-500"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Review Modal */}
      <ImageReviewModal
        open={showImageReviewModal}
        onOpenChange={setShowImageReviewModal}
        uploadedImages={uploadedImages}
        selectedImageOrder={selectedImageOrder}
        setSelectedImageOrder={setSelectedImageOrder}
        draggedIndex={draggedIndex}
        setDraggedIndex={setDraggedIndex}
        dragOverIndex={dragOverIndex}
        setDragOverIndex={setDragOverIndex}
        setUploadedImages={setUploadedImages}
        convertImagesToPdf={convertImagesToPdf}
        onUploadPdf={(pdf: File) => {
          setUploadedFile(pdf);
          setFileName(pdf.name);
          // Clear images since we now have a PDF
          setUploadedImages([]);
          setSelectedImageOrder([]);
        }}
      />

      {/* Tutorial Component */}
      <EvaluateTutorial run={runTutorial} setRun={handleTutorialComplete} />
    </>
  );
};

export default Evaluate;
